import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from "@angular/core";
import { TradableChartService } from "@main-services/tradable-chart.service";
import { Tradable } from "@models/interfaces/tradable";
import { filter, first } from "rxjs/operators";
import { Candle } from "@models";
import { CHART_UPDATE_INTERVAL } from "@helpers/constants";
import { BehaviorSubject } from "rxjs";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Timespan } from "@helpers/const/timespan";

@UntilDestroy()
@Component({
	selector: "simple-chart",
	templateUrl: "./simple-chart.component.html",
	styleUrls: ["./simple-chart.component.scss"],
	standalone: false
})
export class SimpleChartComponent implements AfterViewInit, OnDestroy {
	@Input() public chartWidth: number = 136;
	@Input() public chartHeight: number = 42;
	@Input() public ngClassLine: "noColorCoding" | "increaseColor" | "decreaseColor" | "noChangeColor";

	public coordinates: BehaviorSubject<PointCoordinate[]> = new BehaviorSubject([]);

	private chartUpdateInterval: ReturnType<typeof setTimeout> = null;
	@ViewChild("chartLine", { static: true }) private simpleChart: ElementRef = null;

	public constructor(private chartService: TradableChartService) {}

	private _timespan: Timespan = Timespan.Day;

	@Input("timespan")
	public set timespan(value: Timespan) {
		if (this._timespan !== value) {
			this._timespan = value;
			this.getUpdates();
		}
	}

	private _tradable: Tradable = null;

	public get tradable(): Tradable {
		return this._tradable;
	}

	@Input()
	public set tradable(tradable: Tradable) {
		this._tradable = tradable;
	}

	public ngAfterViewInit(): void {
		this.getUpdates();
		this.initAutoUpdates();
		this.coordinates.pipe(untilDestroyed(this)).subscribe((coordinates) => {
			const points: string = coordinates.map((c) => [c.x, c.y].join(",")).join(" ");
			this.simpleChart.nativeElement.setAttribute("points", points);
		});
	}

	public ngOnDestroy(): void {
		if (this.chartUpdateInterval) {
			clearInterval(this.chartUpdateInterval);
		}
	}

	private getUpdates(): void {
		this.chartService
			.getUpdates(this.tradable, this._timespan)
			.pipe(
				filter((candles) => candles.length !== 0),
				first()
			)
			.subscribe((candles: Candle[]) => {
				const points: number[] = candles.map((candle) => this.chartService.calculateValue(candle));
				const min: number = Math.min(...points);
				const max: number = Math.max(...points);
				this.coordinates.next(
					points.map((point: number, index: number) =>
						this.getCoordinate(point, min, max, points.length, index)
					)
				);
			});
	}

	private initAutoUpdates(): void {
		this.chartUpdateInterval = setInterval(() => {
			this.chartService.updateChart(this.tradable, this._timespan);
			this.getUpdates();
		}, CHART_UPDATE_INTERVAL);
	}

	private getCoordinate(point: number, min: number, max: number, length: number, index: number): PointCoordinate {
		const x: number = (index / length) * this.chartWidth; // X position on the chart
		let y: number = this.chartHeight / 2 + 1; // Centered by default
		if (min !== max) {
			// In case there were changes in the current timespan
			const unit: number = (this.chartHeight - 4) / (max - min);
			y = unit * (point - min); // Y position on the chart
			y = this.chartHeight - y + 1; // At this point the chart is upside-down, must be flipped
		}
		return { x, y };
	}
}

interface PointCoordinate {
	x: number;
	y: number;
}
